import { css } from "@emotion/css";
import { Checkbox, ExternalLink, FileUpload } from "@octopusdeploy/design-system-components";
import { fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { PackageResource, Repository } from "@octopusdeploy/octopus-server-client";
import { useEffect } from "react";
import { createLegacyPackageUploadStartedEvent, createLegacyUploadPackageCancelledEvent, createLegacyUploadPackageSaveEvent, useTrackLegacyPackageUploadEvent } from "~/areas/library/components/BuiltInRepository/amplitudeAnalytics";
import { Form, useForm } from "~/components/form/Form/Form";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import { workflows } from "~/workflows/workflows";
import { supportedPackageTypes } from "./supportedPackageTypes";
interface UploadPackageWorkflowProps {
    completeWorkflow: (output: PackageResource) => void;
    IO?: (repository: Repository) => {
        uploadPackageFile: (file: File, replace: boolean) => Promise<PackageResource>;
    };
}
const initialModel: {
    file: File | null;
    replace: boolean;
} = { file: null, replace: false };
const defaultIO: Required<UploadPackageWorkflowProps>["IO"] = (repository: Repository) => ({
    uploadPackageFile: (pkgFile, replace) => repository.Packages.upload(pkgFile, replace),
});
export function UploadPackageWorkflow({ completeWorkflow, IO = defaultIO }: UploadPackageWorkflowProps) {
    const trackLegacyUploadPackageEvent = useTrackLegacyPackageUploadEvent();
    useEffect(() => {
        trackLegacyUploadPackageEvent(createLegacyPackageUploadStartedEvent());
    }, [trackLegacyUploadPackageEvent]);
    const { model, setModel, formProps, createSaveAction } = useForm({
        initialModel,
        formName: "Upload Package",
        onSubmit: async (repository: Repository, model) => {
            if (model.file) {
                return IO(repository).uploadPackageFile(model.file, model.replace);
            }
        },
        updateFormModelAfterSubmit: () => initialModel,
        afterSubmit: async (_, output) => {
            if (output) {
                trackLegacyUploadPackageEvent(createLegacyUploadPackageSaveEvent());
                completeWorkflow(output);
            }
        },
    });
    const saveAction = createSaveAction({ saveButtonLabel: "Upload", saveButtonBusyLabel: "Uploading..." });
    return (<WorkflowContent.Dialog workflow={workflows.uploadPackageWorkflow} title={uploadPackageWorkflowTitle} primaryAction={saveAction} onCancel={() => trackLegacyUploadPackageEvent(createLegacyUploadPackageCancelledEvent())}>
            <Form {...formProps} expandAllOnMount={true}>
                <p className={noteStyles}>
                    Package name must have a version number and no spaces e.g <code>hello-world.1.0.0.zip</code>
                    <br />
                    <ExternalLink href="SupportedPackages">Supported formats</ExternalLink>: {supportedPackageTypes}.
                </p>
                <p className={noteStyles}>
                    Octopus is not a full NuGet repository and does not allow NuGet clients to connect to this feed. See our <ExternalLink href={"BuiltInFeed"}>built-in repository documentation</ExternalLink> for further information.
                </p>
                <div className={dropzoneAreaStyles}>
                    <FileUpload label={"Add a package to deploy containing all required files"} onChange={(file) => setModel((prev) => ({ ...prev, file: file[0] }))}/>
                </div>
                <Checkbox label="Replace this version if it exists" value={model.replace} onChange={(replace) => setModel((prev) => ({ ...prev, replace }))}/>
            </Form>
        </WorkflowContent.Dialog>);
}
// Note: Consider migrating the <div /> to <Note /> once the latter's legacy styles have been updated
const noteStyles = css({
    marginTop: space[8],
    marginBottom: space[8],
    fontSize: fontSize.small,
    color: themeTokens.color.text.primary,
    a: {
        color: themeTokens.color.text.link.default,
    },
});
const dropzoneAreaStyles = css({
    margin: "10px 0",
});
export const uploadPackageWorkflowTitle = "Upload Package";
